// FILE: PullRequestFailure.tsx
// Purpose: How this section shows a failure — what happened, and what the reader can do next.
// Layer: Component
// Exports: PullRequestFailure
//
// One component for every failure the section produces, because a reader does not care which
// transport disappointed them:
//
//   - A `PullRequestOperationError` carries the server's own reason — from the diff route's JSON
//     body, or from the `{ kind: "failed" }` answer the four websocket methods give — so it
//     becomes an instruction: which screen fixes a missing token, that a rate limit is a wait.
//   - Anything else is a failure of the call itself: an authorization refusal, or a defect. There
//     is no reason to read off it, so its message is shown as it arrived.

import type { PullRequestErrorReason } from "@taskin-code/contracts";

import { Button } from "~/components/ui/button";
import { CircleAlertIcon } from "~/lib/icons";
import { PullRequestOperationError } from "~/lib/pullRequestsReactQuery";
import {
  describePullRequestErrorReason,
  readPullRequestErrorMessage,
} from "./pullRequestPresentation";

export function PullRequestFailure({
  error,
  fallbackTitle,
  context,
  reason,
  onRetry,
}: {
  error: unknown;
  fallbackTitle: string;
  /**
   * Whose failure this is — a project title, when the banner sits in a list that spans several.
   * Kept separate from the title because the title becomes the *reason* as soon as one is known,
   * and "GitHub rejected the token" is useless without knowing which project's token.
   */
  context?: string | undefined;
  /** For a failure that arrived as data rather than as a throw — a project row in `errors[]`. */
  reason?: PullRequestErrorReason | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const resolvedReason =
    reason ?? (error instanceof PullRequestOperationError ? error.reason : undefined);
  const described = resolvedReason ? describePullRequestErrorReason(resolvedReason) : null;
  // Only when the reason could not be translated. Otherwise the guidance above already says what
  // to do, and the server's own sentence says it a second time — in the server's language, not the
  // interface's, so the banner ends up bilingual and repetitive for no added information.
  const detail = described ? "" : readPullRequestErrorMessage(error, "");

  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/6 px-3 py-2.5">
      <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-500" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs font-medium text-foreground">
          {context ? <span className="text-muted-foreground">{context} — </span> : null}
          {described?.title ?? fallbackTitle}
        </p>
        {described?.guidance ? (
          <p className="text-[11px] text-muted-foreground">{described.guidance}</p>
        ) : null}
        {detail.length > 0 ? (
          <p className="break-words text-[11px] text-muted-foreground/80">{detail}</p>
        ) : null}
        {onRetry ? (
          <Button size="xs" variant="outline" className="mt-1.5" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </div>
    </div>
  );
}
